import {
  Form,
  FormCentralizer,
  PasswordField,
  SubmitButton,
} from '@/components';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { z } from 'zod';

import { useUpdatePassword } from '../_hooks/password';
import { UpdatePasswordSchema } from '../_models/password';

type FormValues = z.infer<typeof UpdatePasswordSchema>;

export const UpdatePasswordContent: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { mutateAsync } = useUpdatePassword();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    resolver: zodResolver(UpdatePasswordSchema),
  });

  const onSubmit = async (values: FormValues) => {
    await mutateAsync(values);
    await Promise.all([
      openMessage('success', t('message-update-completed')),
      (async () => {
        navigate('/profile');
      })(),
    ]);
  };

  return (
    <FormCentralizer>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        autoComplete="off"
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <PasswordField
          name="currentPassword"
          label={t('current-password')}
          control={control}
          errors={errors}
        />

        <PasswordField
          name="newPassword"
          label={t('new-password')}
          control={control}
          errors={errors}
        />

        <PasswordField
          name="confirmPassword"
          label={t('confirm-password')}
          control={control}
          errors={errors}
        />

        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      </Form>
    </FormCentralizer>
  );
};
